<template>
  <div class="mobileViewStyle budget-detail">
    <!-- 费用预算详情：查看模式 / 编辑模式（新建也是编辑模式） -->
    <div class="detail-body">
      <!-- ==================== 查看模式 ==================== -->
      <list-card v-if="isView" :label="detail.budgetName">
        <template #desc>
          <span class="type-tag" :class="'tag-' + typeTag(detail.budgetType)">{{ typeLabel(detail.budgetType) }}</span>
          <span class="status-tag" :class="'tag-' + statusTagType(detail.budgetStatus)">{{ detail.budgetStatus }}</span>
          <span class="edit-btn" @click="toEdit">编辑</span>
        </template>
        <data-column label="预算编号">{{ detail.budgetNo || "—" }}</data-column>
        <data-column label="预算人">{{ detail.employeeName || "—" }}</data-column>
        <data-column label="所属部门">{{ detail.departTitle || "—" }}</data-column>
        <data-column label="预算类型">{{ typeLabel(detail.budgetType) }}</data-column>
        <data-column label="预算节点">{{ budgetPeriod(detail) }}</data-column>
        <data-column label="预算金额">{{ money(detail.budgetAmount) }}</data-column>
        <data-column label="已使用">{{ money(detail.usedAmount) }}</data-column>
        <data-column label="可用余额">{{ money(detail.balanceAmount) }}</data-column>
        <data-column label="执行进度">
          <div class="progress">
            <span class="track"><i :style="{width: clampedRate(detail.usedRate), background: barColor(detail.usedRate)}"></i></span>
            <span class="pct">{{ rateText(detail.usedRate) }}</span>
          </div>
        </data-column>
        <data-column label="执行状态">
          <span class="status-tag" :class="'tag-' + statusTagType(detail.budgetStatus)">{{ detail.budgetStatus }}</span>
        </data-column>
        <data-column label="编制人">{{ detail.creatorName || "—" }}</data-column>
        <data-column label="创建时间">{{ dateTime(detail.createdTime) }}</data-column>
        <data-column v-if="detail.remark" label="备注">{{ detail.remark }}</data-column>
      </list-card>

      <!-- ==================== 编辑模式 ==================== -->
      <list-card v-else label="预算信息" type="form">
        <data-input label="预算人">
          <mobile-search-select v-model="editData.employeeId" :query="queryEmployeePage"
                                label-column="employeeName" code-column="id"
                                placeholder="请选择预算人" search-placeholder="职员名称/手机号"
                                :display-text="employeeName" @change="onEmployeeChange">
            <!-- 职员按「头像 + 名称 / 手机号」两行显示（预览模板 B） -->
            <template #item="{item}">
              <div class="mss-item mss-item-avatar-row">
                <div class="mss-item-avatar">
                  <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
                  <yq-icon v-else name="element-ui_user"/>
                </div>
                <div class="mss-item-info">
                  <div class="mss-item-main">{{ item.employeeName }}</div>
                  <div class="mss-item-sub">{{ item.employeePhone || "无联系电话" }}</div>
                </div>
              </div>
            </template>
          </mobile-search-select>
        </data-input>
        <!-- 所属部门由预算人自动带出，只读（后端以职员实际的主部门为准，不接受前端传值） -->
        <data-input label="所属部门">
          <span class="row-text" :class="{placeholder: !editData.departName}">{{ editData.departName || "选择预算人后自动带出" }}</span>
        </data-input>
        <data-input label="预算类型">
          <mobile-select class="row-select" :clearable="false" v-model="editData.budgetType" placeholder="请选择预算类型" @change="onTypeChange">
            <mobile-option v-for="opt in typeOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
          </mobile-select>
        </data-input>
        <data-input label="预算年度">
          <mobile-select class="row-select" :clearable="false" v-model="editData.budgetYear" placeholder="请选择预算年度">
            <mobile-option v-for="opt in yearOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
          </mobile-select>
        </data-input>
        <data-input v-if="Number(editData.budgetType) === 2" label="预算季度">
          <mobile-select class="row-select" :clearable="false" v-model="editData.budgetQuarter" placeholder="请选择季度">
            <mobile-option v-for="opt in quarterOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
          </mobile-select>
        </data-input>
        <data-input v-if="Number(editData.budgetType) === 3" label="预算月份">
          <mobile-select class="row-select" :clearable="false" v-model="editData.budgetMonth" placeholder="请选择月份">
            <mobile-option v-for="opt in monthOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
          </mobile-select>
        </data-input>
        <data-input label="预算金额(元)" v-model="editData.budgetAmount" desc="" placeholder="请输入预算金额"/>
        <data-input label="备注" v-model="editData.remark" desc="" placeholder="选填"/>
      </list-card>
    </div>

    <div class="detail-footer" v-if="!isView">
      <mobile-button @click="cancelEdit">取消</mobile-button>
      <mobile-button el-type="primary" @click="saveData">保存</mobile-button>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import expenseBudgetApi from "@/api/dz-crm/expense/expenseBudgetApi";
import commonApi from "@/api/commonApi";

/** 预算类型，与后端 BudgetNode.TYPE_* 一一对应 */
const TYPE_LABEL = {1: "年度", 2: "季度", 3: "月度"};
const TYPE_TAG = {1: "primary", 2: "success", 3: "info"};
/** 执行状态 → 标签色 */
const STATUS_TAG = {"正常": "success", "预警": "warning", "超支": "danger"};

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "EBD",
  components: {
    MobileButton, MobileSearchSelect, MobileOption, MobileSelect, DataColumn, DataInput, ListCard
  },
  data() {
    const thisYear = new Date().getFullYear();
    return {
      mode: "view",  // view（查看）/ edit（编辑，新建时也是它）
      detail: {},
      editData: {},
      employeeName: "", // 预算人回显（组件内维护选中项，这里只在首屏给个初始文案）
      typeOptions: [
        {label: "年度", value: 1},
        {label: "季度", value: 2},
        {label: "月度", value: 3},
      ],
      yearOptions: [thisYear + 1, thisYear, thisYear - 1].map(y => ({label: String(y), value: y})),
      quarterOptions: [1, 2, 3, 4].map(q => ({label: "第" + "一二三四"[q - 1] + "季度", value: q})),
      monthOptions: Array.from({length: 12}, (_, i) => ({label: (i + 1) + "月", value: i + 1})),
      pageTitle: "",
    };
  },
  computed: {
    isView() {
      return this.mode === "view";
    }
  },
  created() {
    this.pageTitle = document.title;
    document.title = this.route.query.id ? "费用预算详情" : "新增预算";
    if (this.route.query.id) {
      // 点列表进来 = 查看模式
      this.mode = "view";
      this.findById();
    } else {
      // 新建 = 编辑模式（类型默认跟随年预算，与 web 端一致）
      this.mode = "edit";
      this.initNewBudget();
    }
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    findById() {
      expenseBudgetApi.findById(this.route.query.id).then(res => {
        this.detail = res || {};
      });
    },
    // 新建预算：初始化一份空表单
    initNewBudget() {
      this.editData = {
        budgetType: 1,
        budgetYear: new Date().getFullYear(),
        budgetQuarter: null,
        budgetMonth: null,
        employeeId: "",
        departName: "",
        departCode: "",
        budgetAmount: "",
        remark: "",
      };
      this.employeeName = "";
    },
    // 查看模式 -> 编辑模式
    toEdit() {
      this.editData = JSON.parse(JSON.stringify(this.detail));
      this.employeeName = this.editData.employeeName || "";
      this.mode = "edit";
    },
    // 取消编辑：已有预算回查看模式（重新拉一次，丢弃未保存的改动）；新建则回列表
    cancelEdit() {
      if (this.route.query.id) {
        this.mode = "view";
        this.findById();
      } else {
        this.router.back();
      }
    },
    /**
     * 选预算人后按其主部门带出「所属部门」，只用于界面回显。
     * ★ 不把部门提交给后端 —— 服务端以职员的实际 mainDepartCode 为准。
     * ★ 主部门取 departList 里 departType === 1 的那条：职员选择弹窗的数据来自
     *   queryEmployeeListByPage，不含 mainDepartCode，主部门只挂在后端装配的 departList 上。
     */
    onEmployeeChange(record) {
      // 换人后文案交给组件自己维护
      this.employeeName = "";
      if (!record) {
        this.editData.departName = "";
        this.editData.departCode = "";
        return;
      }
      const departList = record.departList || [];
      let main = departList.find(depart => depart.departType === 1);
      if (!main && departList.length) {
        // 没有标记主部门时退而取第一个，避免选了人却什么都不显示
        main = departList[0];
      }
      this.editData.departName = main ? main.departName : "";
      this.editData.departCode = main ? main.departCode : "";
    },
    /** 切换预算类型：清掉另一层级的定位字段，避免把上个月的残留值一起提交 */
    onTypeChange(type) {
      if (Number(type) !== 2) {
        this.editData.budgetQuarter = null;
      }
      if (Number(type) !== 3) {
        this.editData.budgetMonth = null;
      }
    },
    queryEmployeePage(params) {
      return commonApi.queryEmployeeListByPage(params);
    },
    // ---------- 展示 ----------
    typeLabel(budgetType) {
      return TYPE_LABEL[budgetType] || "—";
    },
    typeTag(budgetType) {
      return TYPE_TAG[budgetType] || "info";
    },
    statusTagType(status) {
      return STATUS_TAG[status] || "info";
    },
    /** 预算节点：年度 + 季度/月份 */
    budgetPeriod(item) {
      if (!item.budgetYear) {
        return "—";
      }
      if (Number(item.budgetType) === 2 && item.budgetQuarter) {
        return item.budgetYear + " 第" + "一二三四"[item.budgetQuarter - 1] + "季度";
      }
      if (Number(item.budgetType) === 3 && item.budgetMonth) {
        return item.budgetYear + "-" + String(item.budgetMonth).padStart(2, "0");
      }
      return String(item.budgetYear);
    },
    clampedRate(rate) {
      const value = Number(rate || 0);
      return Math.max(0, Math.min(100, value)).toFixed(1) + "%";
    },
    rateText(rate) {
      return Number(rate || 0).toFixed(1) + "%";
    },
    barColor(rate) {
      const value = Number(rate || 0);
      if (value > 100) {
        return "#e5484d";
      }
      return value >= 85 ? "#faad14" : "#1677ff";
    },
    dateTime(value) {
      return value ? String(value).slice(0, 19) : "—";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return "¥ " + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    saveData() {
      if (!this.editData.employeeId) {
        this.$message.error("请选择预算人");
        return;
      }
      if (!this.editData.budgetType) {
        this.$message.error("请选择预算类型");
        return;
      }
      if (!this.editData.budgetYear) {
        this.$message.error("请选择预算年度");
        return;
      }
      if (this.editData.budgetAmount === "" || this.editData.budgetAmount === null || this.editData.budgetAmount === undefined) {
        this.$message.error("请填写预算金额");
        return;
      }
      const isNew = !this.route.query.id;
      // ★ 不提交 departCode / departName：服务端以职员的实际 mainDepartCode 为准
      const payload = JSON.parse(JSON.stringify(this.editData));
      delete payload.departName;
      delete payload.departCode;
      delete payload.employeeName;
      delete payload.departTitle;
      delete payload.creatorName;
      delete payload.usedAmount;
      delete payload.balanceAmount;
      delete payload.usedRate;
      delete payload.budgetStatus;
      payload.budgetAmount = Number(payload.budgetAmount) || 0;
      payload.budgetType = Number(payload.budgetType) || 1;
      payload.budgetYear = Number(payload.budgetYear);
      // 成功提示由请求拦截器统一弹（saveBudget 是 save 接口）
      expenseBudgetApi.saveBudget(payload).then(() => {
        if (isNew) {
          // 新建：回列表
          this.router.back();
        } else {
          // 编辑已有预算：回查看模式并刷新
          this.mode = "view";
          this.findById();
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.budget-detail{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  // 标题行右侧：类型/状态标签 + 编辑按钮
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .edit-btn{
    flex: none;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .type-tag,
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .tag-primary{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .tag-info{
    color: var(--el-color-info);
    background: var(--el-fill-color-light);
  }
  .tag-warning{
    color: var(--el-color-warning);
    background: var(--el-color-warning-light-9);
  }
  .tag-success{
    color: var(--el-color-success);
    background: var(--el-color-success-light-9);
  }
  .tag-danger{
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
  }
  .row-text{
    color: var(--yq-font-color-base);
    &.placeholder{
      color: var(--yq-font-color-desc);
    }
  }
  .progress{
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    .track{
      flex: none;
      width: 90px;
      height: 8px;
      border-radius: 4px;
      background: var(--yq-background-color);
      overflow: hidden;
      i{
        display: block;
        height: 100%;
        border-radius: 4px;
      }
    }
    .pct{
      color: var(--yq-font-color-desc);
    }
  }
  // 选择类控件放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      display: none;
    }
  }
}
</style>
